import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, Share, StyleSheet, TextInput, View } from 'react-native';
import { Text } from './Texte';
import { LinearGradient } from 'expo-linear-gradient';
import { Button } from './Button';
import { CadrePleinEcran } from './CadrePleinEcran';
import { CircleCheck, ExternalLink, ShieldCheck, X } from './icons';
import { useCvExport } from '../hooks/useCvExport';
import { generateLetterPdf, shareLetterPdf } from '../services/coverLetterPdf';
import { corrigerPoint, lireDossier } from '../services/offres';
import { ouvrirAnnonce } from '../utils/ouvrirAnnonce';
import { t, tBrut } from '../i18n';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';
import type { CoverLetter, GeneratedCv } from '../types';
import type { CvDossier, DossierComplet, PointAConfirmer, ReponseEliminatoire } from '../types/offres';
import { PROPS_LTR } from '../i18n/sens';
import { SensTexteLtr } from './Texte';

type Onglet = 'cv' | 'lettre' | 'reponses';
const ONGLETS: Onglet[] = ['cv', 'lettre', 'reponses'];

export interface RelectureDossierProps {
  dossierId: string;
  onFermer: () => void;
}

/**
 * Écran 5 — la personne VÉRIFIE ce que l'IA a préparé avant d'en faire quoi que ce soit. Rien ne part
 * d'ici : pas de bouton « Envoyer » (lot 3). Les champs modifiés par l'IA sont surlignés, les points à
 * confirmer se confirment ou se corrigent, et une correction est appliquée au contenu stocké — c'est
 * lui que les PDF exportent (décision du 03/10/2026). « Mots-clés couverts », jamais « Score ATS ».
 *
 * Plein écran, comme l'écran de préparation ; barre du haut bleu nuit, pour que les icônes blanches
 * de la barre d'état restent lisibles (un seul style pour tout le parcours).
 */
export function RelectureDossier({ dossierId, onFermer }: RelectureDossierProps) {
  const [etat, setEtat] = useState<'chargement' | 'erreur' | 'pret'>('chargement');
  const [dossier, setDossier] = useState<DossierComplet | null>(null);
  const [onglet, setOnglet] = useState<Onglet>('cv');
  const [enCorrection, setEnCorrection] = useState<{ index: number; texte: string } | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreurCorrection, setErreurCorrection] = useState(false);
  const [erreurExport, setErreurExport] = useState<string | null>(null);
  const { exportCv, exporting, error: erreurExportCv } = useCvExport();

  const charger = useCallback(async () => {
    setEtat('chargement');
    try {
      setDossier(await lireDossier(dossierId));
      setEtat('pret');
    } catch {
      setEtat('erreur');
    }
  }, [dossierId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const traiter = async (index: number, valeur?: string) => {
    setEnvoi(true);
    setErreurCorrection(false);
    try {
      await corrigerPoint(dossierId, valeur === undefined ? { index, statut: 'confirme' } : { index, statut: 'corrige', valeur });
      setEnCorrection(null);
      // Relu plutôt que recalculé ici : le serveur a appliqué la correction au CV ou à la lettre.
      setDossier(await lireDossier(dossierId));
    } catch {
      setErreurCorrection(true);
    } finally {
      setEnvoi(false);
    }
  };

  if (etat !== 'pret' || !dossier) {
    return (
      <CadrePleinEcran fond={colors.grey50} testID="relecture-dossier">
        <BarreFermer onFermer={onFermer} />
        <View style={styles.centre}>
          {etat === 'chargement' ? (
            <ActivityIndicator color={colors.night} />
          ) : (
            <>
              <Text style={styles.texte}>{t('offres.relecture.erreurLecture')}</Text>
              <Button title={t('offres.relecture.reessayer')} variant="secondary" onPress={() => void charger()} />
            </>
          )}
        </View>
      </CadrePleinEcran>
    );
  }

  const { offre, controle, lettre, cv_adapte: cv } = dossier;
  // Adzuna ne transmet qu'un extrait : le dossier a été préparé sans le reste de l'annonce.
  const extrait = offre.source === 'adzuna';
  const couverture = controle?.couverture ?? { couverts: [], manquants: [] };
  const sousTitre = offre.entreprise.nom
    ? t('offres.preparation.pour', { entreprise: offre.entreprise.nom, poste: offre.titre })
    : t('offres.preparation.pourPoste', { poste: offre.titre });

  const exporterLettre = async (l: CoverLetter) => {
    setErreurExport(null);
    try {
      await shareLetterPdf(await generateLetterPdf(l, 'fr'));
    } catch {
      setErreurExport(t('offres.relecture.erreurExport'));
    }
  };

  return (
    <CadrePleinEcran fond={colors.grey50} testID="relecture-dossier">
      <BarreFermer onFermer={onFermer} />
      <ScrollView contentContainerStyle={styles.contenu}>
        <Text style={styles.titre} accessibilityRole="header">
          {t('offres.relecture.titre')}
        </Text>
        <Text style={styles.sousTitre}>{sousTitre}</Text>
        {extrait ? <Text style={styles.discret}>{t('offres.relecture.extrait')}</Text> : null}
        <Text style={styles.couverture}>
          {t('offres.preparation.couverture', { couverts: couverture.couverts.length, total: couverture.couverts.length + couverture.manquants.length })}
        </Text>
        {couverture.manquants.length > 0 ? (
          <Text style={styles.discret}>{t('offres.relecture.manquants', { liste: couverture.manquants.join(', ') })}</Text>
        ) : null}

        {controle && controle.points.length > 0 ? (
          <View style={styles.bloc}>
            <Text style={styles.blocTitre}>{t('offres.relecture.aConfirmer')}</Text>
            {controle.points.map((p, index) => (
              <Point
                key={`${index}-${p.extrait}`}
                point={p}
                index={index}
                correction={dossier.corrections[index]}
                enCorrection={enCorrection?.index === index ? enCorrection.texte : null}
                envoi={envoi}
                onConfirmer={() => void traiter(index)}
                onOuvrirCorrection={() => {
                  setErreurCorrection(false);
                  setEnCorrection({ index, texte: dossier.corrections[index]?.valeur ?? p.original ?? p.extrait });
                }}
                onTexte={(texte) => setEnCorrection({ index, texte })}
                onEnregistrer={(texte) => void traiter(index, texte)}
                onAnnuler={() => setEnCorrection(null)}
                erreur={erreurCorrection && enCorrection?.index === index}
              />
            ))}
          </View>
        ) : null}

        <View style={styles.onglets} accessibilityRole="tablist">
          {ONGLETS.map((o) => (
            <Pressable
              key={o}
              accessibilityRole="tab"
              accessibilityState={{ selected: onglet === o }}
              onPress={() => setOnglet(o)}
              style={[styles.onglet, onglet === o && styles.ongletActif]}
            >
              <Text style={[styles.ongletTexte, onglet === o && styles.ongletTexteActif]}>{t(`offres.relecture.onglets.${o}`)}</Text>
            </Pressable>
          ))}
        </View>

        {onglet === 'cv' && cv ? <OngletCv cv={cv} modifies={dossier.champs_modifies ?? []} /> : null}
        {onglet === 'lettre' && lettre ? <OngletLettre lettre={lettre} /> : null}
        {onglet === 'reponses' ? <OngletReponses reponses={dossier.reponses ?? []} /> : null}

        <View style={styles.actions}>
          {cv ? (
            <Button
              title={t('offres.relecture.exporterCv')}
              variant="dark"
              loading={exporting}
              onPress={() =>
                void exportCv({ ...cv, ats_score: 0, advice: [] } as GeneratedCv, {
                  name: lettre?.candidat.nom_complet,
                  email: lettre?.candidat.email,
                  phone: lettre?.candidat.telephone,
                  outputLanguage: 'fr',
                })
              }
            />
          ) : null}
          {lettre ? <Button title={t('offres.relecture.exporterLettre')} variant="secondary" onPress={() => void exporterLettre(lettre)} /> : null}
          {erreurExportCv || erreurExport ? <Text style={styles.erreur}>{erreurExportCv ?? erreurExport}</Text> : null}
          {offre.urlOrigine ? (
            <Pressable accessibilityRole="button" onPress={() => void ouvrirAnnonce(offre.urlOrigine as string)} style={styles.lien}>
              <Text style={styles.lienTexte}>{t(extrait ? 'offres.relecture.voirOffreComplete' : 'offres.relecture.voirOffre')}</Text>
              <ExternalLink size={18} color={colors.night} />
            </Pressable>
          ) : null}
        </View>

        <View style={styles.pied}>
          <ShieldCheck size={18} color={colors.grey500} />
          <Text style={styles.discret}>{t('offres.preparation.pied')}</Text>
        </View>
      </ScrollView>
    </CadrePleinEcran>
  );
}

function BarreFermer({ onFermer }: { onFermer: () => void }) {
  // Même dégradé bleu nuit que l'écran de préparation : un seul habillage pour tout le parcours.
  return (
    <LinearGradient colors={[colors.night, colors.navyDeep]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={styles.barre} testID="relecture-barre-haute">
      <Pressable accessibilityRole="button" accessibilityLabel={t('offres.relecture.fermer')} onPress={onFermer} style={styles.rond}>
        <X size={22} color={colors.white} />
      </Pressable>
    </LinearGradient>
  );
}

interface PointProps {
  point: PointAConfirmer;
  index: number;
  correction?: { statut: 'confirme' | 'corrige'; valeur?: string };
  enCorrection: string | null;
  envoi: boolean;
  erreur: boolean;
  onConfirmer: () => void;
  onOuvrirCorrection: () => void;
  onTexte: (texte: string) => void;
  onEnregistrer: (texte: string) => void;
  onAnnuler: () => void;
}

function Point({ point, index, correction, enCorrection, envoi, erreur, onConfirmer, onOuvrirCorrection, onTexte, onEnregistrer, onAnnuler }: PointProps) {
  return (
    <View style={styles.point}>
      <Text style={styles.pointRaison}>{t(`offres.relecture.raisons.${point.raison}`)}</Text>
      <Text style={styles.texte}>{correction?.statut === 'corrige' ? correction.valeur : point.extrait}</Text>
      {correction ? (
        <View style={styles.pointEtat}>
          <CircleCheck size={18} color={colors.success} />
          <Text style={styles.pointEtatTexte}>{t(correction.statut === 'corrige' ? 'offres.relecture.corrige' : 'offres.relecture.confirme')}</Text>
        </View>
      ) : null}
      {enCorrection !== null ? (
        <View style={styles.correction}>
          <TextInput
            value={enCorrection}
            onChangeText={onTexte}
            multiline
            style={styles.champ}
            testID={`correction-${index}`}
            accessibilityLabel={t('offres.relecture.champCorrection')}
          />
          {erreur ? <Text style={styles.erreur}>{t('offres.relecture.erreurCorrection')}</Text> : null}
          <View style={styles.boutons}>
            <Button title={t('offres.relecture.enregistrer')} variant="dark" loading={envoi} disabled={!enCorrection.trim()} onPress={() => onEnregistrer(enCorrection)} />
            <Button title={t('offres.relecture.annuler')} variant="secondary" onPress={onAnnuler} />
          </View>
        </View>
      ) : correction ? null : (
        <View style={styles.boutons}>
          <Button title={t('offres.relecture.exact')} variant="secondary" disabled={envoi} onPress={onConfirmer} />
          {/* Une question de la lettre n'a pas de passage à remplacer : rien à corriger. */}
          {point.chemin ? <Button title={t('offres.relecture.corriger')} variant="secondary" disabled={envoi} onPress={onOuvrirCorrection} /> : null}
        </View>
      )}
    </View>
  );
}

function Surligne({ chemin, modifie, children, style }: { chemin: string; modifie: boolean; children: string; style?: object }) {
  return (
    <Text style={[style, modifie && styles.surligne]} testID={modifie ? `cv-modifie-${chemin}` : undefined}>
      {children}
    </Text>
  );
}

function OngletCv({ cv, modifies }: { cv: CvDossier; modifies: string[] }) {
  const estModifie = (chemin: string) => modifies.includes(chemin);
  return (
    <View style={styles.document} {...PROPS_LTR}>
      <SensTexteLtr>
        <Text style={styles.legende}>{tBrut('offres.relecture.legende')}</Text>
        <Surligne chemin="headline" modifie={estModifie('headline')} style={styles.docTitre}>
          {cv.headline}
        </Surligne>
        {cv.experiences.map((e, i) => (
          <View key={`${e.company}-${i}`} style={styles.docBloc}>
            <Text style={styles.docSousTitre}>
              {e.role} · {e.company}
            </Text>
            <Text style={styles.discret}>{[e.start_date, e.end_date].filter(Boolean).join(' – ')}</Text>
            {e.bullets.map((b, j) => (
              <Surligne key={j} chemin={`experiences.${i}.bullets.${j}`} modifie={estModifie(`experiences.${i}.bullets.${j}`)} style={styles.texte}>
                {b}
              </Surligne>
            ))}
          </View>
        ))}
        {cv.education.length > 0 ? (
          <View style={styles.docBloc}>
            <Text style={styles.docSousTitre}>{tBrut('offres.relecture.formation')}</Text>
            {cv.education.map((f, i) => (
              <Text key={i} style={styles.texte}>
                {[f.degree, f.school, f.date].filter(Boolean).join(' · ')}
              </Text>
            ))}
          </View>
        ) : null}
        {cv.skills.length > 0 ? (
          <View style={styles.docBloc}>
            <Text style={styles.docSousTitre}>{tBrut('offres.relecture.competences')}</Text>
            <Surligne chemin="skills" modifie={estModifie('skills')} style={styles.texte}>
              {cv.skills.join(' · ')}
            </Surligne>
          </View>
        ) : null}
        {cv.languages.length > 0 ? (
          <View style={styles.docBloc}>
            <Text style={styles.docSousTitre}>{tBrut('offres.relecture.langues')}</Text>
            <Text style={styles.texte}>{cv.languages.join(' · ')}</Text>
          </View>
        ) : null}
      </SensTexteLtr>
    </View>
  );
}

/** Texte brut de la lettre, pour la feuille de partage (qui propose « Copier »). */
function texteLettre(l: CoverLetter): string {
  return [l.lieu_date, l.objet ? `${tBrut('offres.relecture.objet')} ${l.objet}` : '', l.lettre.formule_appel, ...l.lettre.paragraphes, l.lettre.formule_politesse, l.lettre.signature]
    .filter((x) => x && x.trim())
    .join('\n\n');
}

function OngletLettre({ lettre }: { lettre: CoverLetter }) {
  const [echecCopie, setEchecCopie] = useState(false);
  const copier = async () => {
    setEchecCopie(false);
    try {
      // Feuille de partage plutôt qu'un module presse-papiers : elle propose « Copier », sans code natif
      // de plus (décision du 03/10/2026).
      await Share.share({ message: texteLettre(lettre) });
    } catch {
      setEchecCopie(true);
    }
  };
  return (
    <View style={styles.document} {...PROPS_LTR}>
      <SensTexteLtr>
        <Text style={styles.discret}>{lettre.lieu_date}</Text>
        <Text style={styles.docSousTitre}>
          {tBrut('offres.relecture.objet')} {lettre.objet}
        </Text>
        <Text style={styles.texte}>{lettre.lettre.formule_appel}</Text>
        {lettre.lettre.paragraphes.map((p, i) => (
          <Text key={i} style={styles.texte}>
            {p}
          </Text>
        ))}
        <Text style={styles.texte}>{lettre.lettre.formule_politesse}</Text>
        <Text style={styles.texte}>{lettre.lettre.signature}</Text>
        <Button title={tBrut('offres.relecture.copierLettre')} variant="secondary" onPress={() => void copier()} />
        {echecCopie ? <Text style={styles.erreur}>{tBrut('offres.relecture.erreurCopie')}</Text> : null}
      </SensTexteLtr>
    </View>
  );
}

function reponseLisible(r: ReponseEliminatoire): string {
  if (!r.reponse) return tBrut('offres.relecture.reponses.aRenseigner');
  const { code, valeur } = r.reponse;
  if (code === 'oui' || code === 'non') return tBrut(`offres.relecture.reponses.${code}`);
  if (code === 'date') return tBrut('offres.relecture.reponses.date', { date: String(valeur ?? '') });
  if (code === 'rythme') return tBrut(`offres.rythmesComplets.${String(valeur)}`, { defaultValue: String(valeur ?? '') });
  return tBrut('offres.relecture.reponses.age', { age: String(valeur ?? '') });
}

function OngletReponses({ reponses }: { reponses: ReponseEliminatoire[] }) {
  if (reponses.length === 0) return <Text style={styles.discret}>{t('offres.relecture.reponses.aucune')}</Text>;
  return (
    <View style={styles.document} {...PROPS_LTR}>
      <SensTexteLtr>
        {reponses.map((r, i) => (
          <View key={`${r.type}-${i}`} style={styles.reponse}>
            <Text style={styles.docSousTitre}>{r.libelle}</Text>
            <Text style={[styles.texte, !r.reponse && styles.aRenseigner]}>{reponseLisible(r)}</Text>
          </View>
        ))}
      </SensTexteLtr>
    </View>
  );
}

const styles = StyleSheet.create({
  barre: { flexDirection: 'row', justifyContent: 'flex-end', paddingHorizontal: screenPadding, paddingVertical: spacing.sm },
  rond: {
    minWidth: 44,
    minHeight: 44,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.inkSoft,
  },
  centre: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing.lg, padding: screenPadding },
  contenu: { padding: screenPadding, gap: spacing.lg },
  titre: { fontSize: typography.h1.size, lineHeight: typography.h1.lineHeight, fontWeight: typography.h1.weight, color: colors.navy },
  sousTitre: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },
  couverture: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night },
  bloc: { gap: spacing.sm },
  blocTitre: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night },
  point: { gap: spacing.sm, padding: spacing.lg, borderRadius: radius.lg, backgroundColor: colors.warningSoft },
  pointRaison: { fontSize: typography.small.size, fontWeight: '700', color: colors.night },
  pointEtat: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  pointEtatTexte: { fontSize: typography.small.size, fontWeight: '600', color: colors.success },
  correction: { gap: spacing.sm },
  champ: {
    minHeight: spacing.xxl * 3,
    padding: spacing.md,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.grey200,
    backgroundColor: colors.white,
    fontSize: typography.body.size,
    color: colors.night,
    textAlignVertical: 'top',
  },
  boutons: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  onglets: { flexDirection: 'row', gap: spacing.xs, padding: spacing.xs, borderRadius: radius.pill, backgroundColor: colors.white },
  onglet: { flex: 1, minHeight: 44, alignItems: 'center', justifyContent: 'center', borderRadius: radius.pill },
  ongletActif: { backgroundColor: colors.night },
  ongletTexte: { fontSize: typography.bodyMd.size, fontWeight: '600', color: colors.night },
  ongletTexteActif: { color: colors.white },
  // Document en français : jamais retourné en droite à gauche (i18n/sens.ts).
  document: { direction: 'ltr', gap: spacing.md, padding: spacing.lg, borderRadius: radius.lg, backgroundColor: colors.white },
  legende: { fontSize: typography.small.size, color: colors.grey500 },
  docTitre: { fontSize: typography.h2.size, lineHeight: typography.h2.lineHeight, fontWeight: typography.h2.weight, color: colors.navy },
  docBloc: { gap: spacing.xs },
  docSousTitre: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.night },
  texte: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.night },
  surligne: { backgroundColor: colors.corailSoft },
  discret: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  reponse: { gap: spacing.xs },
  aRenseigner: { color: colors.corail, fontWeight: '600' },
  actions: { gap: spacing.md },
  erreur: { fontSize: typography.small.size, color: colors.error },
  lien: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.sm, minHeight: 44 },
  lienTexte: { fontSize: typography.bodyMd.size, fontWeight: '600', color: colors.night, textDecorationLine: 'underline' },
  pied: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.sm },
});
